import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Instagram } from "lucide-react";
import { api, fmtDate } from "../../lib/api";
import { Badge } from "../../components/Pill";

const STATUS_TONES = { New: "petal", Contacted: "gold", Closed: "ink" };
const NEXT_STATUS = { New: "Contacted", Contacted: "Closed", Closed: "New" };

export default function FeaturedTab() {
  const [items, setItems] = useState(null);

  const load = () => api("/admin/featured").then(setItems).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const setStatus = async (f) => {
    const status = NEXT_STATUS[f.status || "New"];
    try {
      await api(`/admin/featured/${f.id}`, { method: "PATCH", body: { status } });
      setItems(items.map((x) => (x.id === f.id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feature request?")) return;
    try {
      await api(`/admin/featured/${id}`, { method: "DELETE" });
      toast.success("Request deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid="featured-tab">
      <h2 className="font-display uppercase text-2xl md:text-3xl text-ink">Feature Requests</h2>
      <p className="mt-2 text-sm text-neutral-500">Brands, couples and vendors who want to be featured, with their chosen plan. Tap the status to move a lead along.</p>

      <div className="mt-8 space-y-5">
        {!items ? (
          <p className="py-6 font-elegant italic text-xl text-gold">Loading</p>
        ) : items.length === 0 ? (
          <p className="py-6 text-sm text-neutral-500" data-testid="featured-empty">No feature requests yet.</p>
        ) : (
          items.map((f) => (
            <div key={f.id} data-testid={`featured-row-${f.id}`} className="bg-white border-2 border-gold/10 rounded-[1.5rem] p-5 md:p-6">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone="petal">{f.kind}</Badge>
                    {f.plan && <Badge tone="ink">{f.plan}</Badge>}
                    {f.paid && <Badge tone="gold">Paid</Badge>}
                    <button
                      onClick={() => setStatus(f)}
                      data-testid={`featured-status-${f.id}`}
                      title="Tap to change status"
                      className="transition-transform duration-200 hover:scale-105"
                    >
                      <Badge tone={STATUS_TONES[f.status || "New"]}>{f.status || "New"}</Badge>
                    </button>
                    {f.city && <Badge tone="white">{f.city}</Badge>}
                    <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-ink/40">{fmtDate(f.created_at)}</span>
                  </div>
                  <p className="mt-3 font-display uppercase text-lg text-ink">{f.name}</p>
                  <p className="text-sm text-gold break-all">{f.email}</p>
                  {f.instagram && (
                    <a
                      href={`https://www.instagram.com/${f.instagram.replace(/^@/, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`featured-ig-${f.id}`}
                      className="mt-1 inline-flex items-center gap-1.5 text-xs text-rose hover:text-gold transition-colors duration-300"
                    >
                      <Instagram size={13} /> {f.instagram}
                    </a>
                  )}
                  {f.message && <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{f.message}</p>}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <a
                    href={`mailto:${f.email}?subject=${encodeURIComponent("Your feature request to The Brides of India")}&body=${encodeURIComponent(`Hi ${f.name},\n\nThank you for reaching out to The Brides of India about "${f.plan || "getting featured"}".\n\n`)}`}
                    data-testid={`featured-respond-${f.id}`}
                    aria-label="Respond by email"
                    title="Respond by email"
                    className="w-10 h-10 border-2 border-ink/10 rounded-full flex items-center justify-center text-ink/50 hover:border-gold hover:text-gold transition-colors duration-300"
                  >
                    <Mail size={15} />
                  </a>
                  <button onClick={() => remove(f.id)} data-testid={`featured-delete-${f.id}`} aria-label="Delete"
                    className="w-10 h-10 border-2 border-ink/10 rounded-full flex items-center justify-center text-ink/50 hover:border-red-400 hover:text-red-500 transition-colors duration-300">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
